import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DashboardLayout from '../components/DashboardLayout';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { api, endpoints } from '../api/config';
import { getDateInputProps } from '../utils/dateConstraints';

const getDefaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
};

const RESERVED_FIELDS = new Set(['item', 'total_quantity']);

/** Field key = customer name (only remapped if it collides with a base column). */
const customerFieldKey = (name) => (RESERVED_FIELDS.has(name) ? `customer:${name}` : name);

// Column sizing — measure real glyph widths for fixed column widths.
const HEADER_FONT = '700 0.875rem Inter, Roboto, Helvetica, Arial, sans-serif';
const CELL_FONT = '400 0.875rem Inter, Roboto, Helvetica, Arial, sans-serif';
const HEADER_EXTRA_PX = 40; // cell padding + sort/menu icon reserve
const CELL_EXTRA_PX = 24;   // horizontal cell padding
const ITEM_SAMPLE_LIMIT = 400;

let _measureCtx = null;
const measureTextPx = (text, font) => {
  if (typeof document === 'undefined') return String(text ?? '').length * 8;
  if (!_measureCtx) {
    const canvas = document.createElement('canvas');
    _measureCtx = canvas.getContext('2d');
  }
  _measureCtx.font = font;
  return _measureCtx.measureText(String(text ?? '')).width;
};

const widthForHeader = (label) =>
  Math.ceil(measureTextPx(label, HEADER_FONT) + HEADER_EXTRA_PX);

const widthForItemColumn = (rows) => {
  let maxPx = measureTextPx('Item', HEADER_FONT) + HEADER_EXTRA_PX;
  const limit = Math.min(rows.length, ITEM_SAMPLE_LIMIT);
  for (let i = 0; i < limit; i += 1) {
    const w = measureTextPx(rows[i]?.item, CELL_FONT) + CELL_EXTRA_PX;
    if (w > maxPx) maxPx = w;
  }
  return Math.ceil(maxPx);
};

const ReportStat = ({ icon, label, value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: { xs: 1.25, sm: 1.75 },
      py: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        color: 'primary.main',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '1.05rem',
          lineHeight: 1.15,
          color: 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          mt: 0.15,
          fontSize: '0.68rem',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

/** Flatten nested customers map; every customer column gets a numeric qty (0 if missing). */
const flattenRows = (items, customerNames = []) =>
  (Array.isArray(items) ? items : []).map((row) => {
    const flat = {
      item: row.item,
      total_quantity: Number(row.total_quantity || 0),
    };
    for (const name of customerNames) {
      flat[customerFieldKey(name)] = 0;
    }
    const customers = row.customers;
    if (customers && typeof customers === 'object') {
      for (const [name, qty] of Object.entries(customers)) {
        const n = Number(qty);
        flat[customerFieldKey(name)] = Number.isFinite(n) ? n : 0;
      }
    }
    return flat;
  });

const ItemSalesReport = () => {
  const defaults = getDefaultDates();
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [appliedFrom, setAppliedFrom] = useState(defaults.from);
  const [appliedTo, setAppliedTo] = useState(defaults.to);
  const [restrictedToCustomers, setRestrictedToCustomers] = useState(null);
  const [rows, setRows] = useState([]);
  const [customerColumns, setCustomerColumns] = useState([]);
  const [hiddenCustomers, setHiddenCustomers] = useState(() => new Set());
  const [hideZeroSales, setHideZeroSales] = useState(true);
  const [sortBy, setSortBy] = useState('total_quantity');
  const [sortDir, setSortDir] = useState('desc');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const filtersDirty = dateFrom !== appliedFrom || dateTo !== appliedTo;

  const fetchReport = useCallback(async (from, to) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const params = {
        date_from: from,
        date_to: to,
        by_customer: true,
        limit: 20000,
        offset: 0,
      };

      const res = await api.get(endpoints.itemSalesReport, {
        params,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const data = res.data || {};
      const nextColumns = Array.isArray(data.customerColumns) ? data.customerColumns : [];
      setRows(flattenRows(data.items, nextColumns));
      setCustomerColumns(nextColumns);
      setHiddenCustomers(new Set());
      setRestrictedToCustomers(
        Array.isArray(data.restrictedToCustomers) ? data.restrictedToCustomers : null
      );
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || controller.signal.aborted) {
        return;
      }
      console.error('Error fetching item sales report:', err);
      const message =
        err.response?.data?.detail || err.response?.data?.message || err.message ||
        'Failed to load item sales report';
      setError(message);
      setRows([]);
      setCustomerColumns([]);
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (appliedFrom && appliedTo) fetchReport(appliedFrom, appliedTo);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [appliedFrom, appliedTo, fetchReport]);

  const handleApply = useCallback(() => {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) {
      setError('From date must be on or before To date');
      return;
    }
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  }, [dateFrom, dateTo]);

  const handleRefresh = useCallback(() => {
    fetchReport(appliedFrom, appliedTo);
  }, [fetchReport, appliedFrom, appliedTo]);

  const displayRows = useMemo(() => {
    if (!hideZeroSales) return rows;
    return rows.filter((r) => Number(r.total_quantity) > 0);
  }, [rows, hideZeroSales]);

  const visibleCustomerColumns = useMemo(
    () => customerColumns.filter((name) => !hiddenCustomers.has(name)),
    [customerColumns, hiddenCustomers]
  );

  const sortOptions = useMemo(() => {
    const options = [
      { value: 'total_quantity', label: 'Total units' },
      { value: 'item', label: 'Item' },
    ];
    return options;
  }, [visibleCustomerColumns]);

  // If the active sort column was hidden, fall back to Total units.
  useEffect(() => {
    if (!sortOptions.some((o) => o.value === sortBy)) {
      setSortBy('total_quantity');
    }
  }, [sortOptions, sortBy]);

  const sortedRows = useMemo(() => {
    const field = sortBy;
    const dir = sortDir === 'asc' ? 1 : -1;
    const copy = [...displayRows];
    copy.sort((a, b) => {
      const av = a?.[field];
      const bv = b?.[field];
      if (field === 'item') {
        return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { sensitivity: 'base' }) * dir;
      }
      const an = Number(av || 0);
      const bn = Number(bv || 0);
      if (an === bn) {
        return String(a?.item ?? '').localeCompare(String(b?.item ?? ''), undefined, { sensitivity: 'base' });
      }
      return (an - bn) * dir;
    });
    return copy;
  }, [displayRows, sortBy, sortDir]);

  const stats = useMemo(() => {
    const withSales = rows.filter((r) => Number(r.total_quantity) > 0).length;
    const totalUnits = rows.reduce((sum, r) => sum + Number(r.total_quantity || 0), 0);
    return { items: rows.length, withSales, totalUnits };
  }, [rows]);

  const summaryMetrics = useMemo(() => {
    const showingPartial = hideZeroSales && stats.items !== stats.withSales;
    return [
      {
        key: 'rows',
        label: showingPartial ? 'Items shown' : 'Items',
        value: displayRows.length.toLocaleString(),
        icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
      },
      ...(showingPartial
        ? [{
            key: 'catalog',
            label: 'In catalog',
            value: stats.items.toLocaleString(),
            icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />,
          }]
        : []),
      {
        key: 'units',
        label: 'Total units',
        value: stats.totalUnits.toLocaleString(),
        icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />,
      },
      {
        key: 'customers',
        label: visibleCustomerColumns.length !== customerColumns.length
          ? `Customers (${visibleCustomerColumns.length} visible)`
          : 'Customers',
        value: customerColumns.length.toLocaleString(),
        icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} />,
      },
    ];
  }, [
    hideZeroSales,
    stats.items,
    stats.withSales,
    stats.totalUnits,
    displayRows.length,
    customerColumns.length,
    visibleCustomerColumns.length,
  ]);

  const handleVisibleCustomersChange = useCallback((e) => {
    const selected = e.target.value;
    // Sentinel values from the Select all / Clear actions
    if (selected.includes('__all__')) {
      setHiddenCustomers(new Set());
      return;
    }
    if (selected.includes('__none__')) {
      setHiddenCustomers(new Set(customerColumns));
      return;
    }
    setHiddenCustomers(new Set(customerColumns.filter((name) => !selected.includes(name))));
  }, [customerColumns]);

  const columns = useMemo(() => {
    const itemWidth = widthForItemColumn(displayRows);
    const base = [
      {
        accessorKey: 'item',
        header: 'Item',
        width: itemWidth,
        minWidth: itemWidth,
        minSize: itemWidth,
        flex: 0,
        enableSorting: false,
      },
    ];
    const customerCols = visibleCustomerColumns.map((name) => {
      // Qty cells are short ("0", "12"); width tracks the customer header name.
      const w = widthForHeader(name);
      return {
        id: customerFieldKey(name),
        accessorKey: customerFieldKey(name),
        header: name,
        width: w,
        minWidth: w,
        minSize: w,
        flex: 0,
        enableSorting: false,
      };
    });
    return [...base, ...customerCols];
  }, [visibleCustomerColumns, displayRows]);

  const sortToolbar = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.5 }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="item-sales-sort-by-label">Sort by</InputLabel>
          <Select
            labelId="item-sales-sort-by-label"
            value={sortOptions.some((o) => o.value === sortBy) ? sortBy : 'total_quantity'}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ height: 36, fontSize: '0.8125rem' }}
          >
            {sortOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8125rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title={sortDir === 'asc' ? 'Ascending' : 'Descending'}>
          <IconButton
            size="small"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            aria-label="Toggle sort direction"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              width: 36,
              height: 36,
            }}
          >
            {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    ),
    [sortBy, sortDir, sortOptions]
  );

  const grid = useDataGridProTable({
    data: sortedRows,
    loading: isLoading,
    error: error,
    title: 'Item Sales Report',
    columns,
    onDataRefresh: handleRefresh,
    getRowId: (row) => row.item,
    initialPageSize: 100,
    actionButtons: sortToolbar,
    muiTableContainerProps: {
      sx: {
        height: '100%',
        minHeight: 0,
        flex: 1,
      },
    },
  });

  // Pixel-perfect pass once the grid has painted (headers + visible cells).
  useEffect(() => {
    if (isLoading || !columns.length) return undefined;
    const api = grid.apiRef?.current;
    if (!api?.autosizeColumns) return undefined;

    const frame = requestAnimationFrame(() => {
      try {
        api.autosizeColumns({
          includeHeaders: true,
          includeOutliers: false,
          expand: false,
        });
      } catch {
        // autosize is best-effort; measured widths above remain as fallback
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isLoading, columns, displayRows.length, grid.apiRef]);

  return (
    <DashboardLayout>
      <Box
        sx={{
          p: 1.5,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5, fontSize: '1.25rem', flexShrink: 0 }}
        >
          Item Sales Report
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
          <TextField
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={getDateInputProps()}
            sx={{ width: 160, '& .MuiInputBase-root': { height: '36px', fontSize: '0.875rem' } }}
          />
          <TextField
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={getDateInputProps()}
            sx={{ width: 160, '& .MuiInputBase-root': { height: '36px', fontSize: '0.875rem' } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            disabled={isLoading || !filtersDirty}
            sx={{ textTransform: 'none', height: 36 }}
          >
            Apply
          </Button>

          <FormControl size="small" sx={{ minWidth: 260 }} disabled={customerColumns.length === 0}>
            <InputLabel id="customer-columns-label">Customer Columns</InputLabel>
            <Select
              labelId="customer-columns-label"
              multiple
              value={visibleCustomerColumns}
              onChange={handleVisibleCustomersChange}
              input={<OutlinedInput label="Customer Columns" sx={{ height: '36px', fontSize: '0.875rem' }} />}
              renderValue={(selected) =>
                selected.length === 0
                  ? 'None selected'
                  : selected.length === customerColumns.length
                    ? 'All customers'
                    : `${selected.length} of ${customerColumns.length} shown`
              }
              MenuProps={{
                autoFocus: false,
                PaperProps: {
                  sx: {
                    maxHeight: 320,
                    width: 280,
                    mt: 0.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: '0 8px 24px rgba(45, 54, 72, 0.12)',
                    overflow: 'auto',
                  },
                },
                MenuListProps: {
                  dense: true,
                  sx: { py: 0.5 },
                },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem
                value="__all__"
                dense
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 0.75,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemText
                  primary="Select all"
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '0.8125rem' }}
                />
              </MenuItem>
              <MenuItem
                value="__none__"
                dense
                sx={{
                  position: 'sticky',
                  top: 36,
                  zIndex: 1,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 0.5,
                  py: 0.75,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemText
                  primary="Clear all"
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '0.8125rem' }}
                />
              </MenuItem>
              {customerColumns.map((name) => (
                <MenuItem key={name} value={name} dense sx={{ py: 0.35, minHeight: 34 }}>
                  <Checkbox
                    checked={visibleCustomerColumns.includes(name)}
                    size="small"
                    sx={{ p: 0.5, mr: 0.5 }}
                  />
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{
                      fontSize: '0.8125rem',
                      noWrap: true,
                      title: name,
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="zero-sales-label">Rows</InputLabel>
            <Select
              labelId="zero-sales-label"
              value={hideZeroSales ? 'with-sales' : 'all'}
              label="Rows"
              onChange={(e) => setHideZeroSales(e.target.value === 'with-sales')}
              sx={{ height: '36px', fontSize: '0.875rem' }}
            >
              <MenuItem value="with-sales">With sales only</MenuItem>
              <MenuItem value="all">All items</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {!isLoading && !error && (
          <Paper
            variant="outlined"
            sx={{
              mb: 1.5,
              borderRadius: 2,
              borderColor: 'grey.200',
              bgcolor: 'background.paper',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                flexWrap: 'wrap',
                px: 0.5,
                py: 0.25,
              }}
            >
              {summaryMetrics.map((metric, index) => (
                <Box key={metric.key} sx={{ display: 'flex', alignItems: 'stretch' }}>
                  {index > 0 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ display: { xs: 'none', sm: 'block' }, my: 1.25 }}
                    />
                  )}
                  <ReportStat icon={metric.icon} label={metric.label} value={metric.value} />
                </Box>
              ))}

              <Box sx={{ flex: 1, minWidth: 8 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
                <Chip
                  size="small"
                  icon={<ViewColumnOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${visibleCustomerColumns.length}/${customerColumns.length} columns`}
                  variant="outlined"
                  sx={{
                    height: 28,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderColor: 'grey.300',
                    bgcolor: 'grey.50',
                    '& .MuiChip-icon': { color: 'text.secondary' },
                  }}
                />
                {restrictedToCustomers && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`Scoped · ${restrictedToCustomers.length}`}
                    sx={{ height: 28, fontWeight: 600, fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: 'info.dark' }}>Loading item sales…</Typography>
          </Box>
        )}

        {error && !isLoading && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.875rem', flexShrink: 0 }}>{String(error)}</Alert>
        )}

        {!error && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <MuiXDataGridSurface hook={grid} DataGridPro={DataGridPro} />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ItemSalesReport;
